import {
  afterRenderEffect,
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef,
  inject,
  input,
} from '@angular/core';

/**
 * Protocol section chrome state.
 * - `current` → filled draft (yellow / ECAM orange header)
 * - `next` → empty draft (grey / steel header)
 * - `submitted` / `skipped` → completed (green-950 / white)
 * - `skeleton` → loading placeholder
 */
export type EventqProtocolActionSectionState =
  | 'current'
  | 'next'
  | 'submitted'
  | 'skipped'
  | 'skeleton';

@Component({
  selector: 'eventq-protocol-action-section',
  templateUrl: './protocol-action-section.html',
  styleUrl: './protocol-action-section.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    class: 'eventq-protocol-action-section',
    '[class.eventq-protocol-action-section--next]': 'isNext()',
    '[class.eventq-protocol-action-section--submitted]': 'isSubmitted()',
    '[class.eventq-protocol-action-section--skipped]': 'isSkipped()',
    '[class.eventq-protocol-action-section--readonly]': 'isReadonly()',
    '[class.eventq-protocol-action-section--skeleton]': 'isSkeleton()',
    '[class.eventq-protocol-action-section--in-progress]': 'isInProgress()',
    '[class.eventq-protocol-action-section--hide-header]': 'hideHeader()',
    '[attr.data-state]': 'state()',
    '[attr.data-figma-state]': 'figmaStateName()',
    '[attr.aria-busy]': 'isSkeleton() ? "true" : "false"',
  },
})
export class EventqProtocolActionSection {
  private readonly host = inject(ElementRef<HTMLElement>);

  readonly title = input.required<string>();
  readonly state = input<EventqProtocolActionSectionState>('current');
  readonly submittedAt = input<string | undefined>(undefined);
  /** Figma skeleton layout: protocol form chrome vs Additional Information labels. */
  readonly skeletonVariant = input<'protocol' | 'additional-information'>('protocol');
  /** When true, omit the status header (Additional Information and Review Remarks). */
  readonly hideHeader = input(false);

  readonly isSkeleton = computed(() => this.state() === 'skeleton');
  readonly isSubmitted = computed(() => this.state() === 'submitted');
  readonly isNext = computed(() => this.state() === 'next');
  readonly isSkipped = computed(() => this.state() === 'skipped');
  readonly isInProgress = computed(() => this.state() === 'current');
  readonly isReadonly = computed(() => this.state() === 'submitted' || this.state() === 'skipped');
  readonly isAdditionalInformationSkeleton = computed(
    () => this.isSkeleton() && this.skeletonVariant() === 'additional-information',
  );
  readonly showHeader = computed(
    () => !this.hideHeader() && !this.isAdditionalInformationSkeleton(),
  );
  readonly figmaStateName = computed(() => {
    switch (this.state()) {
      case 'current':
        return 'InProgress';
      case 'next':
        return 'Next';
      case 'submitted':
        return 'Submitted';
      case 'skipped':
        return 'Skipped';
      case 'skeleton':
        return 'Skeleton';
    }
  });

  constructor() {
    afterRenderEffect(() => {
      if (!this.isReadonly()) {
        return;
      }
      const root = this.host.nativeElement;
      root.querySelectorAll('input, select, textarea, button').forEach((node: Element) => {
        const el = node as HTMLInputElement;
        el.disabled = true;
        el.setAttribute('disabled', '');
      });
    });
  }
}
